import {Component, Inject, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {BaseFormComponent, Option, ValidationError} from 'nc-shared';
import {combineLatest, Observable, Subscription} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {MatSelect} from '@angular/material/select';
import {CdkVirtualScrollViewport} from '@angular/cdk/scrolling';

@Component({
  selector: 'nc-select',
  templateUrl: './nc-select.component.html',
  styleUrls: ['./nc-select.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR, useExisting: NcSelectComponent, multi: true
  }]
})
export class NcSelectComponent extends BaseFormComponent implements OnInit, OnDestroy {
  @Input() data$: Observable<Array<Option>>;
  @Input() multiple: boolean = false;
  @Input() searchable: boolean = true;
  @Input() translate: boolean = false;
  @Input() removeSelected: boolean = false;
  @Input() searchTextId: string;
  @Input() searchButtonId: string;
  @Input() clearIconId: string;
  @Input() enableSelectAll: boolean = true;
  @Input() comparisonFunction: (option, value) => boolean = (option, value) => {
    // tslint:disable-next-line:triple-equals
    return option == value;
  }
  /** Label of the search placeholder */
  @Input() searchPlaceholderLabel = 'Search';
  /** Label to be shown when no entries are found. Set to null if no message should be shown. */
  @Input() searchNoEntriesFoundLabel = 'No options found';
  @ViewChild(MatSelect) select: MatSelect;
  @ViewChild(CdkVirtualScrollViewport, {static: false})
  cdkVirtualScrollViewPort: CdkVirtualScrollViewport;

  /**  Form control bind to search of select */
  filterControl: FormControl = new FormControl();
  selectControl: FormControl = new FormControl();
  /** Observable that allow display of values and filter them */
  selectData$: Observable<Array<Option>>;
  selectedDataText: string;
  allOptionsSelected = false;


  valueChangedSubscription$: Subscription;
  selectSubscription$: Subscription;

  isInternalChange = false;

  constructor(@Inject('ValidationErrorMap') protected errorMap: Record<string, ValidationError>) {
    super(errorMap);
  }

  ngOnInit(): void {
    this.selectSubscription$ = combineLatest([
      this.data$, this.control.valueChanges.pipe(startWith(this.control.value))
    ]).subscribe(([data, value]) => {
      if (!this.isInternalChange) {
        this.selectControl.patchValue(value, {emitEvent: false});
        this.multiple ? this.setMultipleText(data) : this.setSingleText(data);
      } else {
        this.isInternalChange = false;
      }
    });

    this.valueChangedSubscription$ = combineLatest([
      this.data$, this.selectControl.valueChanges
    ]).subscribe(([data]) => this.valueChanged(data));

    if (this.searchable) {
      this.initDataWithSearch();
    } else {
      this.initData();
    }

    super.ngOnInit();
  }

  ngOnDestroy() {
    this.valueChangedSubscription$.unsubscribe();
    this.selectSubscription$.unsubscribe();
  }

  openChange($event: boolean): void {
    if ($event) {
      this.cdkVirtualScrollViewPort.scrollToIndex(0);
      this.cdkVirtualScrollViewPort.checkViewportSize();
    }
  }

  private initData(): void {
    this.selectData$ = this.data$;
  }

  private initDataWithSearch(): void {
    this.selectData$ = combineLatest([
      this.data$,
      this.filterControl.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([data, filter]) => {
        const lowerCaseSearch = filter.toLowerCase();
        return data.filter(x => x.text.toLowerCase().includes(lowerCaseSearch));
      })
    );
  }

  reset($event: any): void {
    $event.stopPropagation();
    this.selectControl.reset();
    this.control.reset();
    this.isInternalChange = true;
    this.selectedDataText = '';
    this.formGroup.updateValueAndValidity();
  }

  private valueChanged(selectData: Option[]): void {
    if (this.multiple && this.selectControl.value != null && this.enableSelectAll) {
      this.toggleSelectAll(selectData);
    }

    this.multiple ? this.setMultipleText(selectData) : this.setSingleText(selectData);
    this.isInternalChange = true;
    this.control.patchValue(this.selectControl.value);
  }

  private setMultipleText(selectData: Option[]) {
    this.selectedDataText = this.selectControl.value != null
      ? this.selectControl.value.filter(x => x !== 0).map(x => this.findText(x, selectData)).join(', ')
      : '';
  }

  private findText(value, selectData): string {
    return selectData.find(obj => this.comparisonFunction(obj.value, value)).text;
  }

  private setSingleText(selectData: Option[]) {
    const dataText = selectData.find(obj => this.comparisonFunction(obj.value, this.selectControl.value));
    this.selectedDataText = dataText ? dataText.text : '';
  }

  private toggleSelectAll(selectData: Option[]): void {
    const selectedValue = this.selectControl.value;
    const shouldRemoveSelectAllOption = selectedValue.length <= selectData.length && selectedValue.includes(0) && this.allOptionsSelected;

    if (shouldRemoveSelectAllOption){
      this.selectControl.patchValue(this.selectControl.value.filter(value => value !== 0), {emitEvent: false});
      this.allOptionsSelected = false;
    } else if (this.shouldSelectAll(selectedValue, selectData)) {
      this.selectControl.patchValue([...selectData.map(item => item.value), 0], {emitEvent: false});
      this.allOptionsSelected = true;
    } else if (this.allOptionsSelected) {
      this.selectControl.patchValue([], {emitEvent: false});
      this.allOptionsSelected = false;
    }
  }

  private shouldSelectAll(selectedValue, selectData: Option[]): boolean{
    const isAllExceptSelectAllSelected = selectedValue.length === selectData.length && !selectedValue.includes(0);
    const isSelectAllClicked = selectedValue.length <= selectData.length && selectedValue.includes(0);
    return !this.allOptionsSelected && (isAllExceptSelectAllSelected || isSelectAllClicked);
  }
}
